import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Field, EmptyState, ErrorNote } from '../ui';

interface AssetRow {
  id: string;
  kind: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  fileSizeBytes: number;
  provider: string | null;
  createdAt: string;
}
interface CharacterRow {
  id: string;
  name: string;
  role: string;
  gender: string | null;
  references: { id: string; version: number; isMaster: boolean; assetId: string }[];
}
interface PageOption { id: string; internalLabel: string }

const KINDS = ['', 'image', 'video-segment', 'video-combined', 'thumbnail', 'reference'];

export function AssetLibraryScreen({ pageId }: { pageId?: string } = {}) {
  const [tab, setTab] = useState<'assets' | 'characters'>('assets');
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  const pageFilter = pageId ?? localFilter;
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [kindFilter, setKindFilter] = useState('');
  const [characters, setCharacters] = useState<CharacterRow[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageOption[]);
      setAssets((await window.pagepilot.assets.list(pageFilter || undefined, kindFilter || undefined)) as AssetRow[]);
      setCharacters((await window.pagepilot.characters.list(pageFilter || undefined)) as CharacterRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, [pageFilter, kindFilter]);

  const preview = async (assetId: string) => {
    if (previews[assetId]) return;
    const url = (await window.pagepilot.assets.dataUrl(assetId)) as string | null;
    if (url) setPreviews((p) => ({ ...p, [assetId]: url }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Asset Library</h1>
        <div className="flex gap-1">
          <button onClick={() => setTab('assets')} className={`rounded-full px-3 py-1 text-xs ${tab === 'assets' ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-500'}`}>Media</button>
          <button onClick={() => setTab('characters')} className={`rounded-full px-3 py-1 text-xs ${tab === 'characters' ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-500'}`}>Character References</button>
        </div>
        {!pageId && (
          <select value={localFilter} onChange={(e) => setLocalFilter(e.target.value)} className="ml-auto rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm">
            <option value="">All Pages</option>
            {pages.map((p) => <option key={p.id} value={p.id}>{p.internalLabel}</option>)}
          </select>
        )}
      </div>
      {error && <ErrorNote message={error} />}

      {tab === 'assets' ? (
        <>
          <div className="flex gap-1">
            {KINDS.map((k) => (
              <button key={k || 'all'} onClick={() => setKindFilter(k)} className={`rounded-full px-3 py-1 text-xs ${kindFilter === k ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-500 hover:text-neutral-300'}`}>
                {k || 'all'}
              </button>
            ))}
          </div>
          {assets.length === 0 ? (
            <EmptyState title="No assets yet" hint="Generated images, video segments, thumbnails and reference images are stored here with hashes and dimensions." />
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {assets.map((a) => (
                <Card key={a.id}>
                  <button onClick={() => void preview(a.id)} className="mb-2 flex h-32 w-full items-center justify-center overflow-hidden rounded-lg bg-neutral-800">
                    {previews[a.id] ? (
                      a.mimeType.startsWith('video/') ? <video src={previews[a.id]} controls className="h-full w-full object-cover" /> : <img src={previews[a.id]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-[10px] text-neutral-600">click to preview</span>
                    )}
                  </button>
                  <div className="flex items-center justify-between">
                    <Badge status="BUSY" label={a.kind} />
                    <span className="text-[10px] text-neutral-500">{(a.fileSizeBytes / 1024).toFixed(0)} KB</span>
                  </div>
                  <p className="mt-1 text-[10px] text-neutral-500">
                    {a.width && a.height ? `${a.width}×${a.height}` : ''}{a.durationSec ? ` · ${a.durationSec.toFixed(1)}s` : ''}
                    {a.provider ? ` · ${a.provider}` : ''}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <CharactersTab pageId={pageFilter} pages={pages} characters={characters} previews={previews} onPreview={preview} onChanged={load} />
      )}
    </div>
  );
}

function CharactersTab(props: {
  pageId: string;
  pages: PageOption[];
  characters: CharacterRow[];
  previews: Record<string, string>;
  onPreview: (assetId: string) => void;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('interviewer');
  const [err, setErr] = useState<string | null>(null);

  const uploadMaster = async (characterId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await window.pagepilot.characters.uploadMaster({ characterId, dataUrl: reader.result as string });
        props.onChanged();
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      {err && <ErrorNote message={err} />}
      <div>
        <Button disabled={!props.pageId} title={props.pageId ? '' : 'Select a Page first'} onClick={() => setAdding(true)}>Add character</Button>
      </div>
      {adding && (
        <Card title="New character">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" value={name} onChange={setName} placeholder="Marco (interviewer)" />
            <Field label="Role" value={role} onChange={setRole} placeholder="interviewer / respondent" />
          </div>
          <div className="mt-3 flex gap-2">
            <Button disabled={!name} onClick={async () => {
              await window.pagepilot.characters.create({ pageId: props.pageId, name, role });
              setAdding(false); setName(''); props.onChanged();
            }}>Create</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </Card>
      )}
      {props.characters.length === 0 ? (
        <EmptyState title="No characters" hint="Define recurring interviewers/respondents and attach a locked master image so reels keep the same faces and wardrobe across segments." />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {props.characters.map((c) => {
            const master = c.references.find((r) => r.isMaster) ?? c.references[0];
            return (
              <Card key={c.id}>
                <div className="flex gap-3">
                  <button onClick={() => master && props.onPreview(master.assetId)} className="flex h-24 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-neutral-800">
                    {master && props.previews[master.assetId] ? (
                      <img src={props.previews[master.assetId]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-[9px] text-neutral-600">{master ? 'preview' : 'no image'}</span>
                    )}
                  </button>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-neutral-500">{c.role}{c.gender ? ` · ${c.gender}` : ''}</p>
                    <p className="mt-1 text-[10px] text-neutral-600">{c.references.length} reference{c.references.length === 1 ? '' : 's'}</p>
                    <label className="mt-2 inline-block cursor-pointer text-xs text-brand-300 hover:underline">
                      {master ? 'Replace master image' : 'Upload master image'}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && void uploadMaster(c.id, e.target.files[0])} />
                    </label>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
